"use client";

import { useEffect, useCallback, useRef, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useLiveQuery } from "@/core/hooks/useLiveQuery";
import { useRapport } from "@/core/hooks/useRapport";
import { offlineDb } from "@/core/offline/db";
import type { CustomSection } from "@/core/offline/db";
import { TiptapEditor, type JSONContent, type VariableChip } from "@/components/organisms/tiptap-editor";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft,
  Menu,
  Eye,
  Download,
  FileText,
  CheckCircle2,
  Building2,
  ClipboardList,
  Settings,
  X,
  Plus,
  Trash2,
  ImagePlus,
  Upload,
  GripVertical,
  BookOpen,
  RotateCcw,
} from "lucide-react";
import { compressImage } from "@/core/utils/image";
import { useState } from "react";
import { cn } from "@/lib/utils";
import { generateRapportPdf } from "@/modules/corrosion/report/pdf-generator";
import { downloadBlob } from "@/core/utils/file-download";
import { LibraryDrawer } from "@/modules/corrosion/report/LibraryDrawer";
import { PdfAnnexeCard } from "@/modules/corrosion/report/PdfAnnexeCard";
import { ConclusionSuggestions } from "@/modules/corrosion/report/ConclusionSuggestions";
import { useLibrary } from "@/core/hooks/useLibrary";
import { useRapportAnnexes } from "@/core/hooks/useRapportAnnexes";

// ─── Composants extraits (stables, pas recréés à chaque render) ──

function SectionSeparator({
  afterSection,
  onAddText,
  onAddPhoto,
  onAddPdfAnnexe,
  onAddFromLibrary,
}: {
  afterSection: string;
  onAddText: (afterSection: string) => void;
  onAddPhoto: (afterSection: string) => void;
  onAddPdfAnnexe: (afterSection: string) => void;
  onAddFromLibrary: (afterSection: string) => void;
}) {
  const [open, setOpen] = useState(false);

  return (
    <div className="flex items-center justify-center py-2 group relative">
      <div className="flex-1 border-t border-dashed border-border group-hover:border-primary/30 transition-colors" />
      <button
        onClick={() => setOpen(!open)}
        className="mx-3 w-7 h-7 rounded-full border border-dashed border-border text-muted-foreground hover:border-primary hover:text-primary hover:bg-primary/5 flex items-center justify-center transition-colors"
        title="Ajouter une section"
      >
        <Plus className="size-4" />
      </button>
      <div className="flex-1 border-t border-dashed border-border group-hover:border-primary/30 transition-colors" />

      {open && (
        <div className="absolute top-full z-30 bg-card border border-border rounded-lg shadow-md py-1 min-w-56">
          <button
            onClick={() => { onAddText(afterSection); setOpen(false); }}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left hover:bg-muted"
          >
            <FileText className="size-4 text-muted-foreground" />
            Section texte vide
          </button>
          <button
            onClick={() => { onAddPhoto(afterSection); setOpen(false); }}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left hover:bg-muted"
          >
            <ImagePlus className="size-4 text-muted-foreground" />
            Photo + Analyse
          </button>
          <button
            onClick={() => { onAddPdfAnnexe(afterSection); setOpen(false); }}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left hover:bg-muted"
          >
            <Upload className="size-4 text-muted-foreground" />
            PDF annexe (labo, etc.)
          </button>
          <div className="border-t border-border my-1" />
          <button
            onClick={() => { onAddFromLibrary(afterSection); setOpen(false); }}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left hover:bg-muted text-primary"
          >
            <BookOpen className="size-4" />
            Depuis la librairie…
          </button>
        </div>
      )}
    </div>
  );
}

function CustomSectionEditor({
  section,
  sectionNumber,
  onUpdate,
  onDelete,
}: {
  section: CustomSection;
  sectionNumber: number;
  onUpdate: (id: string, patch: Partial<Pick<CustomSection, "title" | "content">>) => void;
  onDelete: (id: string) => void;
}) {
  // État local pour le titre (évite re-render parent à chaque frappe)
  const [localTitle, setLocalTitle] = useState(section.title);
  const titleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleTitleChange = useCallback(
    (value: string) => {
      setLocalTitle(value);
      if (titleTimerRef.current) clearTimeout(titleTimerRef.current);
      titleTimerRef.current = setTimeout(() => {
        onUpdate(section.id, { title: value });
      }, 800);
    },
    [section.id, onUpdate]
  );

  const handleContentChange = useCallback(
    (content: JSONContent) => {
      onUpdate(section.id, { content });
    },
    [section.id, onUpdate]
  );

  return (
    <section id={`section-custom-${section.id}`} className="mb-10">
      <div className="flex items-center gap-3 mb-4">
        <span className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center text-sm font-bold text-primary">
          {sectionNumber}
        </span>
        <input
          type="text"
          value={localTitle}
          onChange={(e) => handleTitleChange(e.target.value)}
          className="text-xl font-bold bg-transparent border-none outline-none focus:ring-0 flex-1 placeholder:text-muted-foreground/50"
          placeholder="Titre de la section"
        />
        <button
          onClick={() => onDelete(section.id)}
          className="w-8 h-8 rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10 flex items-center justify-center transition-colors"
          title="Supprimer cette section"
        >
          <Trash2 className="size-4" />
        </button>
      </div>
      <TiptapEditor
        initialContent={section.content as JSONContent | undefined}
        onChange={handleContentChange}
        placeholder="Rédigez le contenu de cette section..."
        minHeight="150px"
      />
    </section>
  );
}

function PhotoAnalysisEditor({
  section,
  sectionNumber,
  onUpdate,
  onDelete,
}: {
  section: CustomSection;
  sectionNumber: number;
  onUpdate: (id: string, patch: Partial<Pick<CustomSection, "title" | "content" | "photoBlob">>) => void;
  onDelete: (id: string) => void;
}) {
  const [localTitle, setLocalTitle] = useState(section.title);
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const titleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Générer l'URL d'aperçu du blob photo
  useEffect(() => {
    if (section.photoBlob && section.photoBlob instanceof Blob && section.photoBlob.size > 0) {
      const url = URL.createObjectURL(section.photoBlob);
      setPhotoUrl(url);
      return () => URL.revokeObjectURL(url);
    }
    setPhotoUrl(null);
  }, [section.photoBlob]);

  const handleTitleChange = useCallback(
    (value: string) => {
      setLocalTitle(value);
      if (titleTimerRef.current) clearTimeout(titleTimerRef.current);
      titleTimerRef.current = setTimeout(() => {
        onUpdate(section.id, { title: value });
      }, 800);
    },
    [section.id, onUpdate]
  );

  const handleContentChange = useCallback(
    (content: JSONContent) => {
      onUpdate(section.id, { content });
    },
    [section.id, onUpdate]
  );

  const handlePhoto = useCallback(
    async (file: File) => {
      if (!file.type.startsWith("image/")) return;
      const compressed = await compressImage(file);
      onUpdate(section.id, { photoBlob: compressed });
    },
    [section.id, onUpdate]
  );

  return (
    <section id={`section-custom-${section.id}`} className="mb-10">
      <div className="flex items-center gap-3 mb-4">
        <span className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center text-sm font-bold text-primary">
          {sectionNumber}
        </span>
        <input
          type="text"
          value={localTitle}
          onChange={(e) => handleTitleChange(e.target.value)}
          className="text-xl font-bold bg-transparent border-none outline-none focus:ring-0 flex-1 placeholder:text-muted-foreground/50"
          placeholder="Titre de la section"
        />
        <button
          onClick={() => onDelete(section.id)}
          className="w-8 h-8 rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10 flex items-center justify-center transition-colors"
          title="Supprimer cette section"
        >
          <Trash2 className="size-4" />
        </button>
      </div>

      <div className="flex gap-4">
        {/* Colonne gauche : photo */}
        <div className="w-2/5 shrink-0">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handlePhoto(file);
              e.target.value = "";
            }}
          />

          {photoUrl ? (
            <div className="relative group">
              <img
                src={photoUrl}
                alt="Photo section"
                className="w-full rounded-lg border border-border object-cover max-h-72"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                className="absolute inset-0 bg-black/40 text-white opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-2 text-sm font-medium"
              >
                <Upload className="size-4" />
                Changer
              </button>
            </div>
          ) : (
            <button
              onClick={() => fileInputRef.current?.click()}
              className="w-full aspect-[4/3] rounded-lg border-2 border-dashed border-border hover:border-primary/50 flex flex-col items-center justify-center gap-2 text-muted-foreground hover:text-primary transition-colors"
            >
              <ImagePlus className="size-8" />
              <span className="text-sm">Ajouter une photo</span>
            </button>
          )}
        </div>

        {/* Colonne droite : analyse Tiptap */}
        <div className="flex-1">
          <TiptapEditor
            initialContent={section.content as JSONContent | undefined}
            onChange={handleContentChange}
            placeholder="Rédigez l'analyse de cette photo..."
            minHeight="200px"
          />
        </div>
      </div>
    </section>
  );
}

/** Renuméroter les sections custom : order consécutif par groupe afterSection */
function renumberSections(sections: CustomSection[]): CustomSection[] {
  const groups = new Map<string, CustomSection[]>();
  for (const s of sections) {
    const group = groups.get(s.afterSection) ?? [];
    group.push(s);
    groups.set(s.afterSection, group);
  }
  const result: CustomSection[] = [];
  for (const [, group] of groups) {
    group.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    group.forEach((s, i) => result.push({ ...s, order: i }));
  }
  return result;
}

// Variables dynamiques disponibles dans les éditeurs Tiptap
const CHIPS_SITE: VariableChip[] = [
  { key: "site", label: "Nom du site" },
  { key: "adresse", label: "Adresse" },
  { key: "date", label: "Date campagne" },
];

const CHIPS_STATS: VariableChip[] = [
  { key: "nb_mesures", label: "Nb mesures" },
  // Variable redactionnelle : une phrase par couple reseau x materiau, valeurs
  // min/max/moyenne colorees au code couleur (cf. tiptap-to-pdfmake.ts). C'est
  // la version "par typologie" demandee le 2026-06-03 (on ne melange jamais
  // acier et cuivre : une moyenne globale serait trompeuse).
  { key: "mesures_min_max_moy", label: "Mesures rédigées par réseau" },
];

// Sections fixes du rapport (ordre immuable)
const FIXED_SECTIONS = [
  { id: "couverture", label: "Couverture" },
  { id: "mission", label: "Mission" },
  { id: "methodologie", label: "Méthodologie" },
  { id: "mesures", label: "Mesures" },
  { id: "conclusions", label: "Conclusions" },
  { id: "synthese", label: "Tableau de synthèse" },
] as const;

// IDs des sections fixes après lesquelles on peut insérer une section libre
const INSERTABLE_AFTER = ["couverture", "mission", "methodologie", "mesures", "conclusions"] as const;

export default function RapportEditPage() {
  const { siteId } = useParams<{ siteId: string }>();
  const navigate = useNavigate();
  const site = useLiveQuery(() => offlineDb.sites.get(siteId!), [siteId]);
  const { rapport, createRapport, updateRapport } = useRapport(siteId!);

  // Campagnes du site (pour le sélecteur d'export PDF)
  const siteCampagnes = useLiveQuery(async () => {
    if (!siteId) return [];
    const list = await offlineDb.campagnes.where("siteId").equals(siteId).toArray();
    return list.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [siteId]) ?? [];

  // Campagne sélectionnée pour l'export (défaut : active, ou plus récente clôturée)
  const [pdfCampagneId, setPdfCampagneId] = useState<string | null>(null);
  useEffect(() => {
    if (pdfCampagneId) return; // déjà sélectionnée
    if (siteCampagnes.length === 0) return;
    const active = siteCampagnes.find((c) => c.status === "active");
    setPdfCampagneId(active?.id ?? siteCampagnes[0].id);
  }, [siteCampagnes, pdfCampagneId]);

  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeSection, setActiveSection] = useState("couverture");
  const [saveStatus, setSaveStatus] = useState<"saved" | "saving">("saved");
  const [pdfExporting, setPdfExporting] = useState(false);

  // Valeur par défaut du titre de couverture (Renaud peut la raccourcir/modifier)
  const DEFAULT_TITRE_COUVERTURE =
    "Rapport de mesures d\u2019\u00e9paisseurs par ultrasons, Analyses d\u2019eau et examens m\u00e9tallographiques";

  // État local pour les champs couverture (évite re-render Dexie→Tiptap à chaque frappe)
  const [coverFields, setCoverFields] = useState({
    numero: "",
    destinataire: "",
    destinataireAdresse: "",
    signataireNom: "",
    signataireFonction: "",
    titreCouverture: "",
    sousTitreReseau: "",
    batiment: "",
    notaBene: "",
  });
  const coverInitialized = useRef(false);

  // Compter les mesures du site (modèle B : pin.siteId direct)
  const mesureCount = useLiveQuery(async () => {
    const pins = await offlineDb.pins.where("siteId").equals(siteId!).toArray();
    const pinIds = pins.map((p) => p.id);
    return pinIds.length === 0
      ? 0
      : offlineDb.mesures.where("pinId").anyOf(pinIds).count();
  }, [siteId]) ?? 0;

  // Créer le rapport automatiquement à la première visite
  useEffect(() => {
    if (site && !rapport) {
      createRapport({
        signataireNom: "Renaud MERLE",
        signataireFonction: "Gérant",
      });
    }
  }, [site, rapport, createRapport]);

  // Initialiser les champs couverture depuis le rapport Dexie (une seule fois)
  useEffect(() => {
    if (rapport && !coverInitialized.current) {
      coverInitialized.current = true;
      setCoverFields({
        numero: rapport.numero ?? "",
        destinataire: (rapport.destinataire as string) ?? "",
        destinataireAdresse: (rapport.destinataireAdresse as string) ?? "",
        signataireNom: (rapport.signataireNom as string) ?? "",
        signataireFonction: (rapport.signataireFonction as string) ?? "",
        // Pré-remplir avec la valeur par défaut si le rapport n'a pas encore de titre
        // personnalisé. Renaud peut ensuite la raccourcir ou la réécrire selon la mission.
        titreCouverture:
          (rapport.titreCouverture as string) || DEFAULT_TITRE_COUVERTURE,
        sousTitreReseau: (rapport.sousTitreReseau as string) ?? "",
        batiment: (rapport.batiment as string) ?? "",
        notaBene: (rapport.notaBene as string) ?? "",
      });
    }
  }, [rapport]);

  // État local pour les sections custom (même pattern que coverFields)
  const [localCustomSections, setLocalCustomSections] = useState<CustomSection[]>([]);
  const customInitialized = useRef(false);

  // État du drawer librairie
  const [libraryDrawer, setLibraryDrawer] = useState<{ open: boolean; afterSection: string }>({
    open: false,
    afterSection: "mission",
  });

  const { sections: librarySections } = useLibrary();

  // Annexes PDF : hook + file picker pour insertion in-line via SectionSeparator
  const { addAnnexe, deleteAnnexe, renameAnnexe } = useRapportAnnexes(rapport?.id);
  const pdfFileInputRef = useRef<HTMLInputElement>(null);
  const pendingPdfTargetRef = useRef<string>("conclusions");

  useEffect(() => {
    if (rapport && !customInitialized.current) {
      customInitialized.current = true;
      setLocalCustomSections(rapport.customSections ?? []);
    }
  }, [rapport]);

  // Debounce save helper
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const debouncedSave = useCallback(
    (data: Parameters<typeof updateRapport>[0]) => {
      setSaveStatus("saving");
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(async () => {
        await updateRapport(data);
        setSaveStatus("saved");
      }, 1500);
    },
    [updateRapport]
  );

  // Handlers pour les champs couverture (état local immédiat + debounce Dexie)
  const handleFieldChange = useCallback(
    (field: string, value: string) => {
      setCoverFields((prev) => ({ ...prev, [field]: value }));
      debouncedSave({ [field]: value });
    },
    [debouncedSave]
  );

  const handleMissionChange = useCallback(
    (content: JSONContent) => debouncedSave({ missionText: content }),
    [debouncedSave]
  );

  const handleMethodologieChange = useCallback(
    (content: JSONContent) => debouncedSave({ methodologieText: content }),
    [debouncedSave]
  );

  const handleConclusionsChange = useCallback(
    (content: JSONContent) => debouncedSave({ conclusionsText: content }),
    [debouncedSave]
  );

  // ─── Sections fixes : masquage non destructif ──────────────
  // L'utilisateur peut masquer une section fixe. Le contenu reste en BDD,
  // simplement non rendu dans l'editeur et le PDF. Reaffichage a tout moment.
  const hiddenSections = useMemo<string[]>(
    () => (Array.isArray(rapport?.hiddenSections) ? rapport.hiddenSections : []),
    [rapport?.hiddenSections]
  );

  const isFixedHidden = useCallback(
    (id: string) => hiddenSections.includes(id),
    [hiddenSections]
  );

  const setFixedHidden = useCallback(
    (id: string, hide: boolean) => {
      const current = hiddenSections;
      const next = hide
        ? Array.from(new Set([...current, id]))
        : current.filter((s) => s !== id);
      debouncedSave({ hiddenSections: next });
    },
    [hiddenSections, debouncedSave]
  );

  const requestHideFixed = useCallback(
    (id: string, label: string) => {
      const ok = window.confirm(
        `Masquer la section "${label}" de ce rapport ?\n\nLe contenu reste enregistre. Vous pouvez la reafficher a tout moment depuis le sommaire de gauche.`
      );
      if (ok) setFixedHidden(id, true);
    },
    [setFixedHidden]
  );

  // ─── Sections libres : CRUD (état local + save background) ──
  const addCustomSection = useCallback(
    (afterSection: string, type: "text" | "photo-analysis" = "text") => {
      const newSection: CustomSection = {
        id: crypto.randomUUID(),
        title: type === "photo-analysis" ? "Photo et analyse" : "Nouvelle section",
        afterSection,
        type,
      };
      setLocalCustomSections((prev) => {
        const updated = [...prev, newSection];
        debouncedSave({ customSections: updated });
        return updated;
      });
    },
    [debouncedSave]
  );

  const addFromLibrary = useCallback(
    (librarySection: (typeof librarySections)[number], afterSection: string) => {
      const newSection: CustomSection = {
        id: crypto.randomUUID(),
        title: librarySection.title,
        content: librarySection.content,
        afterSection,
        type: "text",
      };
      setLocalCustomSections((prev) => {
        const updated = [...prev, newSection];
        debouncedSave({ customSections: updated });
        return updated;
      });
      setLibraryDrawer({ open: false, afterSection });
    },
    [debouncedSave, librarySections]
  );

  const updateCustomSection = useCallback(
    (sectionId: string, patch: Partial<Pick<CustomSection, "title" | "content" | "photoBlob">>) => {
      setLocalCustomSections((prev) => {
        const updated = prev.map((s) =>
          s.id === sectionId ? { ...s, ...patch } : s
        );
        debouncedSave({ customSections: updated });
        return updated;
      });
    },
    [debouncedSave]
  );

  const deleteCustomSection = useCallback(
    (sectionId: string) => {
      setLocalCustomSections((prev) => {
        const section = prev.find((s) => s.id === sectionId);
        // Si c'est une annexe PDF, supprimer aussi le blob et l'annexe serveur
        if (section?.type === "pdf-annexe" && section.pdfAnnexeId) {
          deleteAnnexe(section.pdfAnnexeId).catch((err) =>
            console.error("[deleteCustomSection] Erreur delete annexe:", err)
          );
        }
        const updated = prev.filter((s) => s.id !== sectionId);
        debouncedSave({ customSections: updated });
        return updated;
      });
    },
    [debouncedSave, deleteAnnexe]
  );

  // ─── Ajout d'une annexe PDF in-line via la modale de section ──
  const openPdfPicker = useCallback((afterSection: string) => {
    pendingPdfTargetRef.current = afterSection;
    pdfFileInputRef.current?.click();
  }, []);

  const handlePdfFileChosen = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      // Reset l'input pour permettre de réimporter le même fichier
      e.target.value = "";
      if (!file || !rapport?.id) return;

      try {
        const newSectionId = crypto.randomUUID();
        const afterSection = pendingPdfTargetRef.current;

        // 1. Créer l'annexe en Dexie + upload serveur (liée au futur newSectionId)
        const annexeId = await addAnnexe(file, newSectionId);

        // 2. Créer la CustomSection liée
        const newSection: CustomSection = {
          id: newSectionId,
          type: "pdf-annexe",
          title: file.name,
          pdfAnnexeId: annexeId,
          afterSection,
        };
        setLocalCustomSections((prev) => {
          const updated = [...prev, newSection];
          debouncedSave({ customSections: updated });
          return updated;
        });
      } catch (err) {
        console.error("[addPdfAnnexe] Erreur:", err);
        alert(err instanceof Error ? err.message : "Erreur d'ajout du PDF");
      }
    },
    [rapport?.id, addAnnexe, debouncedSave]
  );

  // Renommer une annexe PDF (met à jour fileName de l'annexe + title de la section)
  const renamePdfAnnexe = useCallback(
    (section: CustomSection, newName: string) => {
      if (!section.pdfAnnexeId) return;
      renameAnnexe(section.pdfAnnexeId, newName).catch((err) =>
        console.error("[renamePdfAnnexe] Erreur:", err)
      );
      setLocalCustomSections((prev) => {
        const updated = prev.map((s) =>
          s.id === section.id ? { ...s, title: newName } : s
        );
        debouncedSave({ customSections: updated });
        return updated;
      });
    },
    [debouncedSave, renameAnnexe]
  );

  // ─── Déplacement sections custom (drag and drop) ──
  const dragIdRef = useRef<string | null>(null);

  const moveCustomSection = useCallback(
    (draggedId: string, targetAfterSection: string, targetOrder: number) => {
      setLocalCustomSections((prev) => {
        // Retirer la section déplacée
        const dragged = prev.find((s) => s.id === draggedId);
        if (!dragged) return prev;

        const without = prev.filter((s) => s.id !== draggedId);
        // Insérer avec le nouveau afterSection et order
        const updated = [...without, { ...dragged, afterSection: targetAfterSection, order: targetOrder }];
        // Renuméroter chaque groupe pour avoir des ordres consécutifs
        const renumbered = renumberSections(updated);
        debouncedSave({ customSections: renumbered });
        return renumbered;
      });
    },
    [debouncedSave]
  );

  // ─── Sommaire dynamique (sections fixes + custom intercalées) ──
  // Les sections fixes masquees sont exclues du sommaire et leur numero
  // reattribue (cf. globalSectionIndex). Les customs rattachees restent
  // visibles (elles ne dependent pas de la presence du parent fixe).
  const allSections = useMemo(() => {
    const result: Array<{ id: string; label: string; type: "fixed" | "custom"; customId?: string; afterSection?: string; orderInGroup?: number }> = [];
    for (const fixed of FIXED_SECTIONS) {
      if (!hiddenSections.includes(fixed.id)) {
        result.push({ id: fixed.id, label: fixed.label, type: "fixed" });
      }
      // Insérer les custom après cette section fixe (triées par order)
      if (INSERTABLE_AFTER.includes(fixed.id as typeof INSERTABLE_AFTER[number])) {
        const group = localCustomSections
          .filter((cs) => cs.afterSection === fixed.id)
          .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
        for (let i = 0; i < group.length; i++) {
          const cs = group[i];
          result.push({
            id: `custom-${cs.id}`,
            label: cs.title || "Sans titre",
            type: "custom",
            customId: cs.id,
            afterSection: fixed.id,
            orderInGroup: i,
          });
        }
      }
    }
    return result;
  }, [localCustomSections, hiddenSections]);

  // Index global de chaque section dans l'ordre rendu (fixes + customs intercales).
  // Cle = id du sommaire (`custom-<uuid>` pour les customs, id brut pour les fixes)
  // ET id brut du custom pour permettre aux editeurs de retrouver leur numero.
  const globalSectionIndex = useMemo(() => {
    const map = new Map<string, number>();
    allSections.forEach((s, idx) => {
      map.set(s.id, idx);
      if (s.type === "custom" && s.customId) {
        map.set(s.customId, idx);
      }
    });
    return map;
  }, [allSections]);

  // Scroll vers une section
  const scrollToSection = useCallback((sectionId: string) => {
    const el = document.getElementById(`section-${sectionId}`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      setActiveSection(sectionId);
    }
  }, []);

  // Section avec éditeur rempli
  const sectionHasContent = useMemo(() => ({
    couverture: !!(coverFields.numero || coverFields.destinataire),
    mission: !!rapport?.missionText,
    methodologie: !!rapport?.methodologieText,
    mesures: mesureCount > 0,
    conclusions: !!rapport?.conclusionsText,
    synthese: mesureCount > 0,
  }), [rapport, mesureCount, coverFields]);

  if (!site) {
    return (
      <div className="flex h-screen items-center justify-center text-muted-foreground">
        Site introuvable.
      </div>
    );
  }

  // Helper : rendre les sections custom après une section fixe (triées par order)
  const renderCustomSectionsAfter = (fixedId: string) => {
    const sections = localCustomSections
      .filter((s) => s.afterSection === fixedId)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    return sections.map((s) => {
      const sectionNumber = globalSectionIndex.get(s.id) ?? 0;
      return s.type === "photo-analysis" ? (
        <PhotoAnalysisEditor
          key={s.id}
          section={s}
          sectionNumber={sectionNumber}
          onUpdate={updateCustomSection}
          onDelete={deleteCustomSection}
        />
      ) : s.type === "pdf-annexe" ? (
        <PdfAnnexeCard
          key={s.id}
          section={s}
          sectionNumber={sectionNumber}
          onDelete={() => deleteCustomSection(s.id)}
          onRename={(newName) => renamePdfAnnexe(s, newName)}
        />
      ) : (
        <CustomSectionEditor
          key={s.id}
          section={s}
          sectionNumber={sectionNumber}
          onUpdate={updateCustomSection}
          onDelete={deleteCustomSection}
        />
      );
    });
  };

  return (
    <div className="flex flex-col h-screen bg-background overflow-hidden">
      {/* ═══ HEADER ═══ */}
      <header className="h-14 bg-background border-b border-border flex items-center px-4 shrink-0 z-30">
        {/* Burger */}
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="w-10 h-10 flex items-center justify-center rounded-lg hover:bg-muted mr-3"
          title="Menu"
        >
          {sidebarOpen ? <X className="size-5" /> : <Menu className="size-5" />}
        </button>

        {/* Breadcrumb */}
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <span className="text-sm font-semibold text-primary">Ultramesure</span>
          <span className="text-muted-foreground">/</span>
          <span className="text-sm text-muted-foreground">Rapport</span>
          <span className="text-muted-foreground">/</span>
          <h1 className="text-sm font-semibold truncate">{site.name}</h1>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 ml-4">
          <span className="text-xs text-muted-foreground mr-2 flex items-center gap-1">
            {saveStatus === "saved" ? (
              <>
                <CheckCircle2 className="size-3.5 text-corrosion-green" />
                Sauvegardé
              </>
            ) : (
              "Enregistrement..."
            )}
          </span>
          {siteCampagnes.length > 1 && pdfCampagneId && (
            <select
              value={pdfCampagneId}
              onChange={(e) => setPdfCampagneId(e.target.value)}
              title="Campagne à figer dans le PDF"
              className="text-xs rounded-md border border-input bg-transparent px-2 py-1.5 shadow-xs"
            >
              {siteCampagnes.map((c) => {
                const date = new Date(c.date).toLocaleDateString("fr-FR", {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
                });
                const suffix = c.status === "active" ? " (active)" : "";
                return (
                  <option key={c.id} value={c.id}>
                    {date}{suffix}
                  </option>
                );
              })}
            </select>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() =>
              navigate(
                pdfCampagneId
                  ? `/sites/${siteId}/rapport/preview?campagneId=${pdfCampagneId}`
                  : `/sites/${siteId}/rapport/preview`
              )
            }
          >
            <Eye className="size-4" />
            Aperçu PDF
          </Button>
          <Button
            size="sm"
            disabled={pdfExporting}
            onClick={async () => {
              if (!siteId) return;
              setPdfExporting(true);
              try {
                const blob = await generateRapportPdf(siteId, pdfCampagneId ?? undefined);
                const campagneSuffix = pdfCampagneId
                  ? (() => {
                      const c = siteCampagnes.find((x) => x.id === pdfCampagneId);
                      return c ? `_${new Date(c.date).toISOString().slice(0, 10)}` : "";
                    })()
                  : "";
                const fileName = `Rapport_${site?.name ?? "site"}_${rapport?.numero ?? ""}${campagneSuffix}.pdf`
                  .replace(/[/\\?%*:|"<>]/g, "_");
                downloadBlob(blob, fileName);
              } catch (err) {
                console.error("Erreur export PDF:", err);
                alert("Erreur lors de la génération du PDF. Vérifiez la console.");
              } finally {
                setPdfExporting(false);
              }
            }}
          >
            <Download className="size-4" />
            {pdfExporting ? "Génération..." : "Exporter PDF"}
          </Button>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        {/* ═══ SIDEBAR ═══ */}
        <aside
          className={cn(
            "w-64 bg-background border-r border-border flex flex-col shrink-0 z-20 transition-all duration-200",
            !sidebarOpen && "-ml-64"
          )}
        >
          {/* Nav app */}
          <nav className="p-3 border-b border-border">
            <button
              onClick={() => navigate(`/sites/${siteId}`)}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-muted-foreground hover:bg-muted"
            >
              <ArrowLeft className="size-4" />
              Retour au site
            </button>
            <button
              onClick={() => navigate("/sites")}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-muted-foreground hover:bg-muted"
            >
              <Building2 className="size-4" />
              Sites
            </button>
            <button
              onClick={() => navigate("/exports")}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-muted-foreground hover:bg-muted"
            >
              <Download className="size-4" />
              Exports
            </button>
            <button
              onClick={() => navigate("/reglages")}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-muted-foreground hover:bg-muted"
            >
              <Settings className="size-4" />
              Réglages
            </button>
          </nav>

          {/* Sommaire du rapport (dynamique) */}
          <div className="p-3 flex-1 overflow-y-auto">
            <p className="px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
              Sections du rapport
            </p>
            {allSections.map((s, idx) => (
              <div
                key={s.id}
                draggable={s.type === "custom"}
                onDragStart={(e) => {
                  if (s.type === "custom" && s.customId) {
                    dragIdRef.current = s.customId;
                    e.dataTransfer.effectAllowed = "move";
                  }
                }}
                onDragOver={(e) => {
                  if (dragIdRef.current) {
                    e.preventDefault();
                    e.dataTransfer.dropEffect = "move";
                  }
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  if (!dragIdRef.current) return;
                  const draggedId = dragIdRef.current;
                  dragIdRef.current = null;

                  if (s.type === "custom" && s.customId && s.afterSection != null) {
                    // Drop sur une section custom → insérer juste avant elle
                    moveCustomSection(draggedId, s.afterSection, (s.orderInGroup ?? 0));
                  } else if (s.type === "fixed") {
                    // Drop sur une section fixe → insérer en premier après cette section fixe
                    const fixedId = s.id;
                    if (INSERTABLE_AFTER.includes(fixedId as typeof INSERTABLE_AFTER[number])) {
                      moveCustomSection(draggedId, fixedId, -1);
                    }
                  }
                }}
                onDragEnd={() => { dragIdRef.current = null; }}
                onClick={() => scrollToSection(s.id)}
                className={cn(
                  "w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm mb-0.5 text-left cursor-pointer",
                  activeSection === s.id
                    ? "bg-primary/10 text-primary font-semibold"
                    : "text-muted-foreground hover:bg-muted",
                  s.type === "custom" && "cursor-grab active:cursor-grabbing"
                )}
              >
                {s.type === "custom" && (
                  <GripVertical className="size-3.5 text-muted-foreground/50 shrink-0" />
                )}
                <span
                  className={cn(
                    "w-5 h-5 rounded flex items-center justify-center text-xs font-bold shrink-0",
                    s.type === "custom"
                      ? "bg-primary/15 text-primary"
                      : activeSection === s.id
                        ? "bg-primary/15 text-primary"
                        : "bg-muted text-muted-foreground"
                  )}
                >
                  {idx}
                </span>
                <span className="truncate">{s.label}</span>
                {s.type === "fixed" && sectionHasContent[s.id as keyof typeof sectionHasContent] && (
                  <CheckCircle2 className="size-3.5 text-corrosion-green ml-auto shrink-0" />
                )}
                {s.id === "mesures" && mesureCount > 0 && (
                  <span className="ml-auto text-xs text-muted-foreground">
                    {mesureCount}
                  </span>
                )}
              </div>
            ))}

            {hiddenSections.length > 0 && (
              <div className="mt-4 pt-3 border-t border-border">
                <p className="px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                  Sections masquees
                </p>
                {FIXED_SECTIONS.filter((f) => hiddenSections.includes(f.id)).map((f) => (
                  <button
                    key={f.id}
                    type="button"
                    onClick={() => setFixedHidden(f.id, false)}
                    className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm mb-0.5 text-left text-muted-foreground hover:bg-muted"
                    title={`Reafficher la section "${f.label}"`}
                  >
                    <RotateCcw className="size-3.5 shrink-0" />
                    <span className="truncate">{f.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Stats en bas */}
          <div className="p-4 border-t border-border bg-muted/30">
            <div className="text-xs text-muted-foreground space-y-1">
              <div className="flex justify-between">
                <span>N° rapport</span>
                <span className="text-foreground font-medium">
                  {coverFields.numero || "-"}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Mesures</span>
                <span className="text-foreground">{mesureCount}</span>
              </div>
            </div>
          </div>
        </aside>

        {/* ═══ CONTENU PRINCIPAL ═══ */}
        <main className="flex-1 overflow-y-auto">
          <div className="max-w-4xl mx-auto px-8 py-8">
            {/* Section 0 : Couverture */}
            {!isFixedHidden("couverture") && (
            <section id="section-couverture" className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <span className="w-7 h-7 rounded-lg bg-muted flex items-center justify-center text-sm font-bold text-muted-foreground">
                  {globalSectionIndex.get("couverture") ?? 0}
                </span>
                <h2 className="text-xl font-bold">Couverture</h2>
                <span className="text-xs text-muted-foreground ml-2">
                  Générée automatiquement
                </span>
                <button
                  type="button"
                  onClick={() => requestHideFixed("couverture", "Couverture")}
                  className="ml-auto p-1.5 rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
                  title="Masquer cette section"
                >
                  <Trash2 className="size-4" />
                </button>
              </div>
              <div className="bg-card rounded-xl border border-border p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-muted-foreground mb-1">
                      N° de rapport
                    </label>
                    <input
                      type="text"
                      value={coverFields.numero}
                      onChange={(e) => handleFieldChange("numero", e.target.value)}
                      className="w-full h-10 px-3 rounded-lg border border-input text-sm focus:border-primary focus:ring-2 focus:ring-ring/20 outline-none bg-background"
                      placeholder="Ex: UM-2026-001"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-muted-foreground mb-1">
                      Destinataire
                    </label>
                    <input
                      type="text"
                      value={coverFields.destinataire}
                      onChange={(e) => handleFieldChange("destinataire", e.target.value)}
                      className="w-full h-10 px-3 rounded-lg border border-input text-sm focus:border-primary focus:ring-2 focus:ring-ring/20 outline-none bg-background"
                      placeholder="Nom du client"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-1">
                    Adresse destinataire
                  </label>
                  <input
                    type="text"
                    value={coverFields.destinataireAdresse}
                    onChange={(e) =>
                      handleFieldChange("destinataireAdresse", e.target.value)
                    }
                    className="w-full h-10 px-3 rounded-lg border border-input text-sm focus:border-primary focus:ring-2 focus:ring-ring/20 outline-none bg-background"
                    placeholder="Adresse complète"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-1">
                    Titre de couverture
                  </label>
                  <textarea
                    value={coverFields.titreCouverture}
                    onChange={(e) => handleFieldChange("titreCouverture", e.target.value)}
                    rows={2}
                    className="w-full px-3 py-2 rounded-lg border border-input text-sm focus:border-primary focus:ring-2 focus:ring-ring/20 outline-none bg-background resize-y"
                    placeholder="Rapport de mesures d’épaisseurs par ultrasons…"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Adaptez le titre si la mission ne comprend pas toutes les prestations (ex : retirer « Analyses d’eau » si non applicable).
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-muted-foreground mb-1">
                      Sous-titre réseau
                    </label>
                    <input
                      type="text"
                      value={coverFields.sousTitreReseau}
                      onChange={(e) => handleFieldChange("sousTitreReseau", e.target.value)}
                      className="w-full h-10 px-3 rounded-lg border border-input text-sm focus:border-primary focus:ring-2 focus:ring-ring/20 outline-none bg-background"
                      placeholder="Ex : Réseau Change-over, Eau Glacée, ECS…"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-muted-foreground mb-1">
                      Bâtiment
                    </label>
                    <input
                      type="text"
                      value={coverFields.batiment}
                      onChange={(e) => handleFieldChange("batiment", e.target.value)}
                      className="w-full h-10 px-3 rounded-lg border border-input text-sm focus:border-primary focus:ring-2 focus:ring-ring/20 outline-none bg-background"
                      placeholder="Ex : Bâtiment Mollien"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-muted-foreground mb-1">
                      Signataire
                    </label>
                    <input
                      type="text"
                      value={coverFields.signataireNom}
                      onChange={(e) =>
                        handleFieldChange("signataireNom", e.target.value)
                      }
                      className="w-full h-10 px-3 rounded-lg border border-input text-sm focus:border-primary focus:ring-2 focus:ring-ring/20 outline-none bg-background"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-muted-foreground mb-1">
                      Fonction
                    </label>
                    <input
                      type="text"
                      value={coverFields.signataireFonction}
                      onChange={(e) =>
                        handleFieldChange("signataireFonction", e.target.value)
                      }
                      className="w-full h-10 px-3 rounded-lg border border-input text-sm focus:border-primary focus:ring-2 focus:ring-ring/20 outline-none bg-background"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-1">
                    Nota Bene <span className="text-xs text-muted-foreground font-normal">(affiché sous la signature, 2-3 lignes max)</span>
                  </label>
                  <textarea
                    value={coverFields.notaBene}
                    onChange={(e) => handleFieldChange("notaBene", e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 rounded-lg border border-input text-sm focus:border-primary focus:ring-2 focus:ring-ring/20 outline-none bg-background resize-none"
                    placeholder="Ex : Les résultats ne concernent que les canalisations inspectées le jour de l'intervention."
                  />
                </div>
              </div>
            </section>
            )}

            <SectionSeparator onAddText={(a) => addCustomSection(a, "text")} onAddPhoto={(a) => addCustomSection(a, "photo-analysis")} onAddPdfAnnexe={openPdfPicker} onAddFromLibrary={(a) => setLibraryDrawer({ open: true, afterSection: a })} afterSection="couverture" />
            {renderCustomSectionsAfter("couverture")}

            {/* Section 1 : Mission */}
            {!isFixedHidden("mission") && (
            <section id="section-mission" className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <span className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center text-sm font-bold text-primary">
                  {globalSectionIndex.get("mission") ?? 0}
                </span>
                <h2 className="text-xl font-bold">Mission</h2>
                <button
                  type="button"
                  onClick={() => requestHideFixed("mission", "Mission")}
                  className="ml-auto p-1.5 rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
                  title="Masquer cette section"
                >
                  <Trash2 className="size-4" />
                </button>
              </div>
              <TiptapEditor
                initialContent={rapport?.missionText as JSONContent | undefined}
                onChange={handleMissionChange}
                placeholder="Décrivez la mission confiée..."
                minHeight="180px"
                variableChips={CHIPS_SITE}
              />
            </section>
            )}

            <SectionSeparator onAddText={(a) => addCustomSection(a, "text")} onAddPhoto={(a) => addCustomSection(a, "photo-analysis")} onAddPdfAnnexe={openPdfPicker} onAddFromLibrary={(a) => setLibraryDrawer({ open: true, afterSection: a })} afterSection="mission" />
            {renderCustomSectionsAfter("mission")}

            {/* Section 2 : Méthodologie */}
            {!isFixedHidden("methodologie") && (
            <section id="section-methodologie" className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <span className="w-7 h-7 rounded-lg bg-muted flex items-center justify-center text-sm font-bold text-muted-foreground">
                  {globalSectionIndex.get("methodologie") ?? 0}
                </span>
                <h2 className="text-xl font-bold">Méthodologie</h2>
                <button
                  type="button"
                  onClick={() => requestHideFixed("methodologie", "Methodologie")}
                  className="ml-auto p-1.5 rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
                  title="Masquer cette section"
                >
                  <Trash2 className="size-4" />
                </button>
              </div>
              <TiptapEditor
                initialContent={
                  rapport?.methodologieText as JSONContent | undefined
                }
                onChange={handleMethodologieChange}
                placeholder="Décrivez la méthodologie employée..."
                minHeight="250px"
              />
            </section>
            )}

            <SectionSeparator onAddText={(a) => addCustomSection(a, "text")} onAddPhoto={(a) => addCustomSection(a, "photo-analysis")} onAddPdfAnnexe={openPdfPicker} onAddFromLibrary={(a) => setLibraryDrawer({ open: true, afterSection: a })} afterSection="methodologie" />
            {renderCustomSectionsAfter("methodologie")}

            {/* Section 3 : Mesures (preview auto-générée) */}
            {!isFixedHidden("mesures") && (
            <section id="section-mesures" className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <span className="w-7 h-7 rounded-lg bg-muted flex items-center justify-center text-sm font-bold text-muted-foreground">
                  {globalSectionIndex.get("mesures") ?? 0}
                </span>
                <h2 className="text-xl font-bold">Mesures</h2>
                <span className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded-full ml-2">
                  Auto-généré depuis vos données
                </span>
                <button
                  type="button"
                  onClick={() => requestHideFixed("mesures", "Mesures")}
                  className="ml-auto p-1.5 rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
                  title="Masquer cette section"
                >
                  <Trash2 className="size-4" />
                </button>
              </div>
              <div className="bg-card rounded-xl border border-border p-6">
                <div className="flex items-center gap-3 text-muted-foreground">
                  <ClipboardList className="size-5" />
                  <p className="text-sm">
                    {mesureCount > 0
                      ? `${mesureCount} fiche${mesureCount > 1 ? "s" : ""} de mesure seront incluse${mesureCount > 1 ? "s" : ""} automatiquement dans le PDF, avec les photos associées.`
                      : "Aucune mesure enregistrée. Les fiches apparaîtront ici quand vous aurez saisi des mesures."}
                  </p>
                </div>
              </div>
            </section>
            )}

            <SectionSeparator onAddText={(a) => addCustomSection(a, "text")} onAddPhoto={(a) => addCustomSection(a, "photo-analysis")} onAddPdfAnnexe={openPdfPicker} onAddFromLibrary={(a) => setLibraryDrawer({ open: true, afterSection: a })} afterSection="mesures" />
            {renderCustomSectionsAfter("mesures")}

            {/* Section 4 : Conclusions */}
            {!isFixedHidden("conclusions") && (
            <section id="section-conclusions" className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <span className="w-7 h-7 rounded-lg bg-muted flex items-center justify-center text-sm font-bold text-muted-foreground">
                  {globalSectionIndex.get("conclusions") ?? 0}
                </span>
                <h2 className="text-xl font-bold">Conclusions</h2>
                <button
                  type="button"
                  onClick={() => requestHideFixed("conclusions", "Conclusions")}
                  className="ml-auto p-1.5 rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
                  title="Masquer cette section"
                >
                  <Trash2 className="size-4" />
                </button>
              </div>
              <ConclusionSuggestions
                siteId={siteId!}
                onInsert={(section) => addFromLibrary(section, "conclusions")}
              />
              <TiptapEditor
                initialContent={
                  rapport?.conclusionsText as JSONContent | undefined
                }
                onChange={handleConclusionsChange}
                placeholder="Rédigez vos conclusions..."
                minHeight="300px"
                variableChips={[...CHIPS_SITE, ...CHIPS_STATS]}
              />
            </section>
            )}

            <SectionSeparator onAddText={(a) => addCustomSection(a, "text")} onAddPhoto={(a) => addCustomSection(a, "photo-analysis")} onAddPdfAnnexe={openPdfPicker} onAddFromLibrary={(a) => setLibraryDrawer({ open: true, afterSection: a })} afterSection="conclusions" />
            {renderCustomSectionsAfter("conclusions")}

            {/* Section 5 : Tableau de synthèse (auto-généré) */}
            {/* Les annexes PDF sont désormais des sections libres insertables n'importe où */}
            {!isFixedHidden("synthese") && (
            <section id="section-synthese" className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <span className="w-7 h-7 rounded-lg bg-muted flex items-center justify-center text-sm font-bold text-muted-foreground">
                  {globalSectionIndex.get("synthese") ?? 0}
                </span>
                <h2 className="text-xl font-bold">Tableau de synthèse</h2>
                <span className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded-full ml-2">
                  Auto-généré
                </span>
                <button
                  type="button"
                  onClick={() => requestHideFixed("synthese", "Tableau de synthese")}
                  className="ml-auto p-1.5 rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
                  title="Masquer cette section"
                >
                  <Trash2 className="size-4" />
                </button>
              </div>
              <div className="bg-card rounded-xl border border-border p-6">
                <div className="flex items-center gap-3 text-muted-foreground">
                  <FileText className="size-5" />
                  <p className="text-sm">
                    Le tableau de synthèse avec les codes couleur sera généré
                    automatiquement dans le PDF à partir de vos mesures.
                  </p>
                </div>
              </div>
            </section>
            )}
          </div>
        </main>
      </div>

      {/* Drawer librairie */}
      <LibraryDrawer
        open={libraryDrawer.open}
        afterSection={libraryDrawer.afterSection}
        onInsert={(section, afterSection) => addFromLibrary(section, afterSection)}
        onClose={() => setLibraryDrawer((prev) => ({ ...prev, open: false }))}
      />

      {/* Input file cache : declenche par SectionSeparator > "PDF annexe" */}
      <input
        ref={pdfFileInputRef}
        type="file"
        accept="application/pdf"
        className="hidden"
        onChange={handlePdfFileChosen}
      />
    </div>
  );
}
